// src/App.js
import React, { useState } from "react";
import BG from "../../assets/image/png/bg-image.png";
import { Brain, AlertCircle, RefreshCcw } from "lucide-react";
import {
  GraduationCap,
  Briefcase,
  TrendingUp,
  Award,
  Microscope,
  Target,
} from "lucide-react";
import Navbar from "../homepage/Navbar";
import {Footer} from "../../shared";
import { Header } from "../../shared";

const ContactItem = ({ icon, label, value, link }) => {
  const icons = {
    mail: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    ),
    phone: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    ),
    globe: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
      />
    ),
    location: (
      <>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </>
    ),
  };

  return (
    <div className="flex items-start">
      <div className="bg-blue-100 p-3 rounded-full mr-4">
        <svg
          className="w-6 h-6 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {icons[icon]}
        </svg>
      </div>
      <div>
        <p className="font-semibold">{label}</p>
        {link ? (
          <a href={link} className="text-blue-600 hover:underline">
            {value}
          </a>
        ) : (
          <p>{value}</p>
        )}
      </div>
    </div>
  );
};

const DroneWorkshopLanding = () => {
  const [kits, setKits] = useState(25);

  const calculatePrice = () => {
    const basePrice = 4999;
    const totalKits = Math.max(25, kits);
    const freeKits = Math.floor(totalKits / 30) * 5;
    const paidKits = totalKits - freeKits;
    return paidKits * basePrice;
  };

  const calculateSavings = () => {
    return Math.floor(kits / 30) * 5 * 4999;
  };
  const benefitsData = [
    {
      icon: <Target className="text-blue-600 w-6 h-6" />,
      title: "Aligns with NEP 2020",
      desc: "Matches NEP’s push for experiential learning & 21st-century skills.",
    },
    {
      icon: <Briefcase className="text-red-500 w-6 h-6" />,
      title: "Real Career Pathways",
      desc: "Connects students to internships and future careers.",
    },
    {
      icon: <TrendingUp className="text-green-500 w-6 h-6" />,
      title: "Increased Admissions",
      desc: "Attract parents with standout STEM curriculum.",
    },
    {
      icon: <Award className="text-yellow-500 w-6 h-6" />,
      title: "Industry Recognition",
      desc: "Gain credibility through media and academic praise.",
    },
    {
      icon: <Microscope className="text-purple-500 w-6 h-6" />,
      title: "Hands-on Learning",
      desc: "Replaces theory with real-world engagement and practice.",
    },
    {
      icon: <GraduationCap className="text-indigo-500 w-6 h-6" />,
      title: "Future-Ready Focus",
      desc: "Prepares students for tomorrow, not yesterday.",
    },
  ];
  const totalKits = Math.max(25, kits);
  const freeKits = Math.floor(totalKits / 30) * 5;
  const paidKits = totalKits - freeKits;
  const totalPrice = calculatePrice();
  const savings = calculateSavings();

  return (
    <>
      <Header />
      <div className="font-sans">
        {/* Hero Section */}
        <div
          className="relative bg-cover bg-center text-white min-h-[80vh]"
          style={{
            backgroundImage: `url(${BG})`,
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/20 z-0 backdrop-blur-sm" />

          {/* Content */}
          <div className="container mx-auto px-5 py-24 md:py-32 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-snug">
                The Sky is DIY
                <span className="block text-yellow-400 text-xl md:text-3xl mt-2 font-medium">
                  🚀 Where Dreams Take Flight
                </span>
              </h1>
              <p className="text-sm md:text-lg text-white/80 mb-8">
                India’s First Plug-&-Play Drone Workshop for Schools
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="#pricing"
                  className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-semibold py-2.5 px-6 rounded-full text-base shadow-md transition duration-300 hover:scale-105"
                >
                  Book Workshop
                </a>
                <a
                  href="#benefits"
                  className="border border-white hover:bg-white hover:text-blue-900 text-white font-medium py-2.5 px-6 rounded-full text-base transition duration-300"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>

          {/* Floating Drone Image */}
          <div className="absolute top-[35%] right-6 md:right-16 z-0 opacity-30 animate-float-slow">
            <img
              src="/assets/drone-icon.png"
              alt="Floating Drone"
              className="w-16 h-16 md:w-20 md:h-20 drop-shadow-lg"
            />
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-b from-white to-blue-50 py-16">
          <div className="container mx-auto px-4">
            {/* Heading */}
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The Skills Crisis is Real
              </h2>
              <p className="text-md md:text-lg text-gray-600">
                Students are stuck in the past while the world is moving into
                the future.
              </p>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              {/* Card 1 */}
              <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all border border-blue-100">
                <div className="flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4 mx-auto">
                  <Brain className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-blue-700 text-3xl font-extrabold text-center mb-2">
                  45%
                </h3>
                <p className="text-gray-600 text-center text-sm">
                  Skills learned today become obsolete in 5 years
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all border border-red-100">
                <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-4 mx-auto">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-red-600 text-3xl font-extrabold text-center mb-2">
                  61%
                </h3>
                <p className="text-gray-600 text-center text-sm">
                  Students think tech is "not for them"
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all border border-green-100">
                <div className="flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4 mx-auto">
                  <RefreshCcw className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-green-600 text-3xl font-extrabold text-center mb-2">
                  90min
                </h3>
                <p className="text-gray-600 text-center text-sm">
                  To completely transform their mindset
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div
          id="benefits"
          className="py-16 bg-gradient-to-b from-gray-50 to-white"
        >
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Why Schools Choose Us
              </h2>
              <p className="text-gray-600 mt-2 text-base md:text-lg">
                Trusted by 500+ progressive institutions shaping future-ready
                students.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefitsData.map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 text-center"
                >
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-blue-50 rounded-full">
                    {item.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Workshop Experience */}
        <div className="py-24 bg-gradient-to-b from-blue-900 to-blue-950 text-white">
          <div className="container mx-auto px-6">
            {/* Heading */}
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-4">
                90 Minutes That Change Everything
              </h2>
              <p className="text-lg md:text-xl text-white/80">
                Transform mindsets and inspire future innovators in less than
                two hours
              </p>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
              {[
                {
                  step: "Step 1",
                  title: "DIY Drone Build",
                  desc: "Students build their own drone from scratch, learning engineering principles hands-on",
                },
                {
                  step: "Step 2",
                  title: "Flight Experience",
                  desc: "Take their creation to the skies with supervised outdoor flights and real-world testing",
                },
                {
                  step: "Step 3",
                  title: "Interactive Q&A",
                  desc: "Engage with industry experts and get answers to burning questions about tech careers",
                },
                {
                  step: "Step 4",
                  title: "Continued Learning",
                  desc: "Access to Google Classroom-based LMS for ongoing skill development and competitions",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-blue-800 hover:bg-blue-700 transition-colors duration-300 rounded-2xl p-8 shadow-md hover:shadow-xl border-t-4 border-yellow-400"
                >
                  <div className="flex items-center justify-start mb-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-400 text-blue-900 font-extrabold flex items-center justify-center text-lg shadow-md">
                      {index + 1}
                    </div>
                    <span className="ml-4 text-xl font-semibold tracking-wide">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/90">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div
          id="pricing"
          className="py-24 bg-gradient-to-b from-gray-50 to-white"
        >
          <div className="container mx-auto px-6">
            {/* Heading */}
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-4">
                Investment in Your Students’ Future
              </h2>
              <p className="text-lg md:text-xl text-gray-600">
                Equip your students with cutting-edge skills at an affordable
                cost
              </p>
            </div>

            {/* Pricing Card */}
            <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="p-8 md:p-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      DIY Drone Workshop Package
                    </h3>
                    <p className="text-gray-600">
                      Everything included for a transformative experience
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-extrabold text-blue-700">
                      ₹4,999
                    </div>
                    <p className="text-sm text-gray-500">Per DIY Drone Kit</p>
                  </div>
                </div>

                {/* Included + Offer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  {/* Included Features */}
                  <div className="bg-blue-50 p-6 rounded-2xl">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      What’s Included:
                    </h4>
                    <ul className="space-y-3 text-gray-700 text-sm">
                      {[
                        "Complete drone building kit",
                        "90-minute expert-led workshop",
                        "Outdoor flight experience",
                        "Free LMS access with Google Classroom",
                        "Industry-designed curriculum",
                        "Career guidance and mentorship",
                        "Competition preparation resources",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-green-500 mt-1 mr-2">✔</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Offer & Calculator */}
                  <div className="bg-yellow-50 p-6 rounded-2xl">
                    <div className="bg-yellow-500 text-white text-center py-2 px-4 rounded-lg font-medium mb-4">
                      🎁 Special Offer: Order 30 kits, get 5 FREE!
                    </div>
                    <p className="text-gray-700 mb-4 text-sm">
                      Minimum order: 25 kits • Workshop conducted FREE for
                      participating students
                    </p>

                    <div className="mt-4">
                      <label
                        htmlFor="kits"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Number of Kits:
                      </label>
                      <div className="flex items-center">
                        <input
                          type="range"
                          id="kits"
                          min="25"
                          max="100"
                          value={kits}
                          onChange={(e) => setKits(parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer"
                        />
                        <span className="ml-4 font-bold text-gray-900 text-lg">
                          {kits}
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Free kits:</p>
                          <p className="text-green-600 font-semibold">
                            {freeKits}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total savings:</p>
                          <p className="text-green-600 font-semibold">
                            ₹{savings.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-100 p-6 rounded-xl flex flex-col md:flex-row justify-between items-center">
                  <div className="text-sm text-gray-700">
                    <p>
                      Total Kits: <span className="font-bold">{totalKits}</span>{" "}
                      (Incl. {freeKits} free)
                    </p>
                    <p>Price per Kit: ₹4,999</p>
                  </div>
                  <div className="mt-4 md:mt-0 text-right">
                    <div className="text-3xl font-extrabold text-blue-700">
                      ₹{totalPrice.toLocaleString()}
                    </div>
                    <p className="text-gray-500 text-sm">Total Investment</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-blue-900 p-6 text-center">
                <a
                  href="#contact"
                  className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-3 px-8 rounded-full text-lg transition-all duration-300 inline-block"
                >
                  Book Your Workshop Today
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        {/* <div className="py-20 bg-gradient-to-b from-white to-gray-50">
  <div className="container mx-auto px-4">
    <div className="text-center mb-16">
      <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
        Trusted by 500+ Schools
      </h2>
      <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
        Hear from educators who've transformed their STEM programs
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
      {[{
        name: "Rajesh Kumar",
        role: "Principal, Delhi Public School",
        initial: "R",
        quote: "This workshop transformed how our students view technology. The hands-on experience was invaluable.",
      }, {
        name: "Priya Sharma",
        role: "STEM Coordinator, Modern High School",
        initial: "P",
        quote: "Our students haven't stopped talking about their drone-building experience. The perfect blend of education and excitement!",
      }, {
        name: "Anil Gupta",
        role: "Director, Wisdom Academy",
        initial: "A",
        quote: "The alignment with NEP 2020 made this an easy decision. Our students gained practical skills that textbooks can't provide.",
      }].map((item, index) => (
        <div
          key={index}
          className="bg-white shadow-md hover:shadow-xl transition-shadow duration-300 rounded-2xl p-8 border border-gray-100"
        >
          <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
          <p className="text-gray-800 text-base italic mb-6">"{item.quote}"</p>
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center mr-4">
              {item.initial}
            </div>
            <div>
              <p className="text-gray-900 font-semibold">{item.name}</p>
              <p className="text-sm text-gray-500">{item.role}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</div> */}

        {/* CTA Section */}
        <div
          id="contact"
          className="py-20 bg-gradient-to-br from-blue-700 to-indigo-800 text-white"
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-4xl md:text-4xl font-extrabold leading-tight mb-4">
                Ready to Launch Your School's Future?
              </h2>
              <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                This is not just a session—it's the ignition of ambition. It's
                your students' launchpad.
              </p>
            </div>

            <div className="bg-white text-gray-800 rounded-2xl shadow-2xl p-10 md:p-12 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-8 text-center text-blue-900">
                Book Your Workshop Today
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Left Column */}
                <div className="space-y-6">
                  <ContactItem
                    icon="mail"
                    label="Email"
                    value="support@droneplanet.in"
                    link="mailto:support@droneplanet.in"
                  />
                  <ContactItem
                    icon="phone"
                    label="Phone"
                    value="+91 97735 71992"
                    link="tel:+919773571992"
                  />
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <ContactItem
                    icon="globe"
                    label="Website"
                    value="www.droneplanet.in"
                    link="https://www.droneplanet.in"
                  />
                  <ContactItem
                    icon="location"
                    label="Location"
                    value="Gurugram, India"
                  />
                </div>
              </div>

              <div className="mt-10 text-center">
                <a
                  href="mailto:support@droneplanet.in"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-10 rounded-full transition duration-300 inline-block"
                >
                  Send Us an Inquiry
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

function App() {
  return <DroneWorkshopLanding />;
}

export default App;
